import { useState } from 'react';
import { Box, Button, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { navigate } from '../../../redux/navigator/action';
import { ROUTES } from '../../../types/global/routes.types';
import {
    LOYALTY_DISPLAY_FONT,
    LOYALTY_GOLD,
    LOYALTY_ON_VARIANT,
    LOYALTY_SURFACE_HIGHEST,
    loyaltyBodyTextSx,
    loyaltyGoldButtonSx,
    loyaltyHeadlineSx,
    loyaltyLabelTextSx,
} from './loyaltyTheme';
import { copyReferralLink, openReferralEmailShare } from './loyaltyReferralShare';
import { useBuzzbridgeLoyaltyInvite } from './useBuzzbridgeLoyaltyInvite';

export function LoyaltyReferralEntry() {
    const dispatch = useDispatch();
    const { invite } = useBuzzbridgeLoyaltyInvite();
    const [copied, setCopied] = useState(false);

    const goRewards = () => {
        dispatch(navigate({ routes: ROUTES.BUZZ_BRIDGE_REWARDS, skipAuthentication: false }));
    };

    const handleCopy = async () => {
        if (!invite?.referral_link) return;
        const ok = await copyReferralLink(invite.referral_link);
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleEmail = () => {
        if (!invite?.referral_link) return;
        openReferralEmailShare(invite.referral_link);
    };

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '32px',
                bgcolor: '#131313',
                p: { xs: 2.5, md: 3 },
                mb: 0,
                height: '100%',
                transition: 'box-shadow 0.5s',
                '&:hover': { boxShadow: '0 0 40px rgba(254,183,0,0.1)' },
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    top: -48,
                    right: -48,
                    width: 192,
                    height: 192,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,201,101,0.1)',
                    filter: 'blur(48px)',
                    pointerEvents: 'none',
                }}
            />

            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, minWidth: 0, flex: 1 }}>
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: '16px',
                                bgcolor: LOYALTY_SURFACE_HIGHEST,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.35)',
                            }}>
                            <CardGiftcardOutlinedIcon sx={{ color: LOYALTY_GOLD, fontSize: 28 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ ...loyaltyHeadlineSx, color: '#fff', fontSize: 20, mb: 0.5 }}>
                                Referral rewards
                            </Typography>
                            <Typography sx={{ ...loyaltyBodyTextSx, maxWidth: 320, lineHeight: 1.5 }}>
                                Earn points and cash when creators join and subscribe through your link.
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        onClick={goRewards}
                        sx={{
                            ...loyaltyGoldButtonSx,
                            alignSelf: 'flex-start',
                            fontSize: 11,
                            letterSpacing: '0.14em',
                            px: 4,
                            py: 1.75,
                            flexShrink: 0,
                        }}>
                        Manage
                    </Button>
                </Box>

                <Box
                    sx={{
                        bgcolor: 'rgba(0,0,0,0.25)',
                        border: '1px solid rgba(72,72,71,0.35)',
                        borderRadius: '16px',
                        p: { xs: 2, sm: 2.5 },
                    }}>
                    <Typography sx={{ ...loyaltyLabelTextSx, fontSize: 10, mb: 1.5, letterSpacing: '0.2em' }}>
                        Your referral link
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1.5,
                            alignItems: { sm: 'center' },
                        }}>
                        <TextField
                            fullWidth
                            size="small"
                            value={invite?.referral_link ?? ''}
                            placeholder="Loading your referral link…"
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => void handleCopy()}
                                            disabled={!invite?.referral_link}
                                            aria-label="Copy referral link"
                                            sx={{ color: copied ? '#00fdc1' : LOYALTY_GOLD }}>
                                            {copied ? (
                                                <CheckIcon fontSize="small" />
                                            ) : (
                                                <ContentCopyIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: {
                                    fontFamily: LOYALTY_DISPLAY_FONT,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: '#fff',
                                    bgcolor: LOYALTY_SURFACE_HIGHEST,
                                    borderRadius: '12px',
                                    '& fieldset': { borderColor: 'rgba(72,72,71,0.4)' },
                                },
                            }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                            <Button
                                onClick={() => void handleCopy()}
                                disabled={!invite?.referral_link}
                                startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                                sx={{
                                    fontFamily: LOYALTY_DISPLAY_FONT,
                                    fontWeight: 700,
                                    fontSize: 12,
                                    color: LOYALTY_GOLD,
                                    border: '1px solid rgba(255,201,101,0.35)',
                                    borderRadius: '999px',
                                    px: 2.5,
                                    whiteSpace: 'nowrap',
                                    '&:hover': { bgcolor: 'rgba(255,201,101,0.08)' },
                                }}>
                                Copy
                            </Button>
                            <Button
                                onClick={handleEmail}
                                disabled={!invite?.referral_link}
                                startIcon={<EmailOutlinedIcon />}
                                sx={{
                                    fontFamily: LOYALTY_DISPLAY_FONT,
                                    fontWeight: 700,
                                    fontSize: 12,
                                    color: LOYALTY_ON_VARIANT,
                                    border: '1px solid rgba(72,72,71,0.5)',
                                    borderRadius: '999px',
                                    px: 2.5,
                                    whiteSpace: 'nowrap',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                                }}>
                                Email
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
